/**
 * Avans servisi.
 * Personel talep eder → BOSS karar verir → onaylanırsa BOSS para verir → "Ödendi" işaretler.
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, query, where,
  deleteField,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { FsTimestamp } from '@/types'
import { logYaz, type LogRol } from './sistem-log-service'
import { bildirimGonder } from './bildirim-service'

const AVANSLAR = 'avanslar'

// 🚧 TEST MODU: 15 gün kuralını geçici devre dışı bırakır.
// Test bitince false yap (veya bu satırı sil).
const TEST_TARIH_KONTROLU_KAPALI = false

export type AvansDurum =
  | 'bekliyor'
  | 'karsiTeklif'
  | 'onaylandi'
  | 'odendi'
  | 'reddedildi'
  | 'iptal'

export interface Avans {
  id: string
  personelId: string
  talepTutari: number
  onayTutari?: number
  aciklama?: string
  durum: AvansDurum
  ay: string

  talepTarihi: FsTimestamp

  kararTarihi?: FsTimestamp
  kararVerenId?: string
  redSebebi?: string

  karsiTeklifTutari?: number
  karsiTeklifTarihi?: FsTimestamp

  odemeTarihi?: FsTimestamp
  odeyenId?: string

  iptalTarihi?: FsTimestamp
  iptalEdenId?: string
}

export interface AvansTalepData {
  personelId: string
  tutar: number
  aciklama?: string
}

function buAyStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export async function avansTalepEt(data: AvansTalepData): Promise<string> {
  if (data.tutar <= 0) throw new Error('Tutar pozitif olmalı.')

  const aktif = await personelAktifAvans(data.personelId)
  if (aktif) {
    throw new Error('Zaten aktif bir talebin var. Önce onun sonuçlanmasını bekle.')
  }

  // 15 gün kuralı (TEST MODU ile geçici kapatılabilir)
  if (!TEST_TARIH_KONTROLU_KAPALI) {
    const bugun = new Date()
    if (bugun.getDate() < 15) {
      throw new Error('Avans talebi sadece ayın 15\'inden sonra yapılabilir.')
    }
  }

  const ay = buAyStr()
  const buAyTalepler = await personelAyAvanslari(data.personelId, ay)
  const aktifVeOdenmis = buAyTalepler.filter(
    (a) => a.durum === 'odendi' || a.durum === 'onaylandi' ||
           a.durum === 'bekliyor' || a.durum === 'karsiTeklif'
  )
  if (aktifVeOdenmis.length >= 2) {
    throw new Error('Bu ay maksimum 2 avans talebi yaptın.')
  }

  const db = getFirestoreDb()
  const id = doc(collection(db, AVANSLAR)).id

  const docData: Record<string, unknown> = {
    personelId: data.personelId,
    talepTutari: data.tutar,
    durum: 'bekliyor' as AvansDurum,
    ay,
    talepTarihi: nowServer(),
  }
  if (data.aciklama?.trim()) docData.aciklama = data.aciklama.trim()

  await setDoc(doc(db, AVANSLAR, id), docData)

  // Talep bildirimi Cloud Function trigger ile gönderilir (avansTalepTrigger).
  return id
}

export async function personelAktifAvans(personelId: string): Promise<Avans | null> {
  const db = getFirestoreDb()
  const q = query(collection(db, AVANSLAR), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  const aktif = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Avans)
    .find((a) =>
      a.durum === 'bekliyor' ||
      a.durum === 'karsiTeklif' ||
      a.durum === 'onaylandi'
    )
  return aktif ?? null
}

export async function personelAyAvanslari(
  personelId: string,
  ay: string
): Promise<Avans[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, AVANSLAR), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Avans)
    .filter((a) => a.ay === ay)
}

export async function personelAvanslari(personelId: string): Promise<Avans[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, AVANSLAR), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Avans)
    .sort((a, b) => b.talepTarihi.toMillis() - a.talepTarihi.toMillis())
}

export async function tumAvanslar(): Promise<Avans[]> {
  const db = getFirestoreDb()
  const snap = await getDocs(collection(db, AVANSLAR))
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Avans)
    .sort((a, b) => b.talepTarihi.toMillis() - a.talepTarihi.toMillis())
}

export async function avansGetir(id: string): Promise<Avans | null> {
  const db = getFirestoreDb()
  const snap = await getDoc(doc(db, AVANSLAR, id))
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() } as Avans
}

export async function avansOnayla(id: string, kararVerenId: string): Promise<void> {
  const db = getFirestoreDb()
  const docRef = doc(db, AVANSLAR, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) throw new Error('Avans bulunamadı.')
  const veri = snap.data() as Avans
  if (veri.durum !== 'bekliyor') {
    throw new Error('Bu talep zaten karara bağlanmış.')
  }
  await updateDoc(docRef, {
    durum: 'onaylandi' as AvansDurum,
    onayTutari: veri.talepTutari,
    kararTarihi: nowServer(),
    kararVerenId,
  })

  try {
    await bildirimGonder({
      kullaniciId: veri.personelId,
      tipi: 'avansOnayRed',
      aciliyet: 'normal',
      baslik: '✅ Avans onaylandı',
      mesaj: `${veri.talepTutari.toLocaleString('tr-TR')} ₺ avans onaylandı. Ödeme bekleniyor.`,
      link: '/personel',
    })
  } catch (e) { console.warn('[avans] onay bildirimi:', e) }
}

export async function avansReddet(
  id: string,
  kararVerenId: string,
  sebep?: string
): Promise<void> {
  const db = getFirestoreDb()
  const docRef = doc(db, AVANSLAR, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) throw new Error('Avans bulunamadı.')
  const veri = snap.data() as Avans

  const guncel: Record<string, unknown> = {
    durum: 'reddedildi' as AvansDurum,
    kararTarihi: nowServer(),
    kararVerenId,
  }
  if (sebep?.trim()) guncel.redSebebi = sebep.trim()
  await updateDoc(docRef, guncel)

  try {
    await bildirimGonder({
      kullaniciId: veri.personelId,
      tipi: 'avansOnayRed',
      aciliyet: 'normal',
      baslik: '❌ Avans reddedildi',
      mesaj: `${veri.talepTutari.toLocaleString('tr-TR')} ₺ avans talebin reddedildi.` +
        (sebep?.trim() ? ` Sebep: ${sebep.trim()}` : ''),
      link: '/personel',
    })
  } catch (e) { console.warn('[avans] red bildirimi:', e) }
}

export async function avansKarsiTeklif(
  id: string,
  yeniTutar: number,
  kararVerenId: string
): Promise<void> {
  if (yeniTutar <= 0) throw new Error('Tutar pozitif olmalı.')

  const db = getFirestoreDb()
  const docRef = doc(db, AVANSLAR, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) throw new Error('Avans bulunamadı.')
  const veri = snap.data() as Avans

  await updateDoc(docRef, {
    durum: 'karsiTeklif' as AvansDurum,
    karsiTeklifTutari: yeniTutar,
    karsiTeklifTarihi: nowServer(),
    kararVerenId,
  })

  try {
    await bildirimGonder({
      kullaniciId: veri.personelId,
      tipi: 'avansKarsiTeklif',
      aciliyet: 'normal',
      baslik: '↔️ Karşı teklif',
      mesaj: `Avans için ${yeniTutar.toLocaleString('tr-TR')} ₺ karşı teklif yapıldı. Kabul ediyor musun?`,
      link: '/personel',
    })
  } catch (e) { console.warn('[avans] karşı teklif bildirimi:', e) }
}

export async function avansOdendi(id: string, odeyenId: string): Promise<void> {
  const db = getFirestoreDb()
  const docRef = doc(db, AVANSLAR, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) throw new Error('Avans bulunamadı.')
  const veri = snap.data() as Avans
  if (veri.durum !== 'onaylandi') {
    throw new Error('Sadece onaylanmış talep ödenebilir.')
  }
  await updateDoc(docRef, {
    durum: 'odendi' as AvansDurum,
    odemeTarihi: nowServer(),
    odeyenId,
  })

  try {
    await bildirimGonder({
      kullaniciId: veri.personelId,
      tipi: 'avansOdendi',
      aciliyet: 'normal',
      baslik: '💰 Avans ödendi',
      mesaj: `${(veri.onayTutari ?? veri.talepTutari).toLocaleString('tr-TR')} ₺ avans sana ödendi.`,
      link: '/personel',
    })
  } catch (e) { console.warn('[avans] ödendi bildirimi:', e) }
}

export async function avansKarsiTeklifKabul(id: string): Promise<void> {
  const db = getFirestoreDb()
  const docRef = doc(db, AVANSLAR, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) throw new Error('Avans bulunamadı.')
  const veri = snap.data() as Avans
  if (veri.durum !== 'karsiTeklif') {
    throw new Error('Bu talepte karşı teklif yok.')
  }
  await updateDoc(docRef, {
    durum: 'onaylandi' as AvansDurum,
    onayTutari: veri.karsiTeklifTutari,
    kararTarihi: nowServer(),
  })
}

export async function avansKarsiTeklifRed(id: string): Promise<void> {
  await updateDoc(doc(getFirestoreDb(), AVANSLAR, id), {
    durum: 'reddedildi' as AvansDurum,
    kararTarihi: nowServer(),
    redSebebi: 'Personel karşı teklifi reddetti.',
  })
}

export interface AvansKararDegistirOpsiyonlar {
  redSebebi?: string
  karsiTeklifTutari?: number
  yapanIsim?: string
  yapanRol?: LogRol
}

/**
 * BOSS önceki avans kararını değiştirir (onaylandi ↔ reddedildi ↔ karsiTeklif).
 * - onaylandi/reddedildi'den karsiTeklif'e geçişte yeniTutar < talepTutari olmalı.
 * - karsiTeklif durumundayken değişim kabul edilmez (önce personel cevabı bekleniyor).
 * - odendi/iptal/bekliyor durumlarından değişim kabul edilmez.
 */
export async function avansKararDegistir(
  id: string,
  yeniDurum: 'onaylandi' | 'reddedildi' | 'karsiTeklif',
  kararVerenId: string,
  opsiyonlar: AvansKararDegistirOpsiyonlar = {},
): Promise<void> {
  const { redSebebi, karsiTeklifTutari, yapanIsim, yapanRol = 'boss' } = opsiyonlar
  const db = getFirestoreDb()
  const docRef = doc(db, AVANSLAR, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) throw new Error('Avans bulunamadı.')
  const veri = snap.data() as Avans

  if (veri.durum !== 'onaylandi' && veri.durum !== 'reddedildi') {
    throw new Error('Sadece onaylanmış veya reddedilmiş avansın kararı değiştirilebilir.')
  }
  if (veri.durum === yeniDurum) {
    throw new Error('Avans zaten bu durumda.')
  }

  const eskiDurum = veri.durum
  const guncel: Record<string, unknown> = {
    kararVerenId,
  }

  let logTutar: number | undefined
  if (yeniDurum === 'onaylandi') {
    guncel.durum = 'onaylandi' as AvansDurum
    guncel.kararTarihi = nowServer()
    guncel.redSebebi = deleteField()
    logTutar = veri.karsiTeklifTutari ?? veri.talepTutari
    guncel.onayTutari = logTutar
  } else if (yeniDurum === 'reddedildi') {
    guncel.durum = 'reddedildi' as AvansDurum
    guncel.kararTarihi = nowServer()
    guncel.onayTutari = deleteField()
    guncel.redSebebi = redSebebi?.trim() ? redSebebi.trim() : deleteField()
    logTutar = veri.talepTutari
  } else {
    if (!karsiTeklifTutari || karsiTeklifTutari <= 0) {
      throw new Error('Karşı teklif tutarı pozitif olmalı.')
    }
    if (karsiTeklifTutari >= veri.talepTutari) {
      throw new Error('Karşı teklif, talep tutarından düşük olmalı.')
    }
    guncel.durum = 'karsiTeklif' as AvansDurum
    guncel.karsiTeklifTutari = karsiTeklifTutari
    guncel.karsiTeklifTarihi = nowServer()
    guncel.kararTarihi = deleteField()
    guncel.onayTutari = deleteField()
    guncel.redSebebi = deleteField()
    logTutar = karsiTeklifTutari
  }

  await updateDoc(docRef, guncel)

  await logYaz({
    tip: 'avans.kararDegistir',
    baslik: `Avans kararı değiştirildi: ${eskiDurum} → ${yeniDurum}`,
    hedefId: id,
    yapanId: kararVerenId,
    yapanIsim,
    yapanRol,
    degisiklikler: [{ alan: 'durum', eski: eskiDurum, yeni: yeniDurum }],
    ekBilgi: {
      personelId: veri.personelId,
      tutar: logTutar,
    },
  })
}

export async function avansIptal(id: string, iptalEdenId: string): Promise<void> {
  const db = getFirestoreDb()
  const docRef = doc(db, AVANSLAR, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) throw new Error('Avans bulunamadı.')
  const veri = snap.data() as Avans

  if (veri.durum === 'odendi') throw new Error('Ödenmiş avans iptal edilemez.')
  if (veri.durum === 'iptal' || veri.durum === 'reddedildi') {
    throw new Error('Bu talep zaten kapalı.')
  }

  await updateDoc(docRef, {
    durum: 'iptal' as AvansDurum,
    iptalTarihi: nowServer(),
    iptalEdenId,
  })
}
